/**
 * Placeholder colour palette for the MVP renderer.
 *
 * `docs/05_ART_DIRECTION.md` is explicit that art budget is not to be spent
 * before the loop is proven, so these are flat fills, not art. They still have
 * two jobs the MVP depends on:
 *
 * 1. Six biomes distinguishable at a glance, including at continent zoom where a
 *    hex is a few pixels across.
 * 2. Staying *muted* — low saturation, mid lightness. The civilisation layer
 *    (saturated kingdom borders and fills) is drawn on top of these, and docs/05
 *    makes "ownership always reads clearly against the world layer" a hard rule.
 *    A vivid terrain palette would compete with the thing that must win.
 */

import type { Biome, SpecialHex } from "kngdm-shared";

/**
 * Biome fills. Hue carries the biome, and the spread in lightness (forest
 * darkest, desert lightest) is what keeps them separable for the ~8% of players
 * with a colour vision deficiency, for whom the green/brown pairs collapse.
 */
export const BIOME_COLORS: Record<Biome, number> = {
  plains: 0x6d9155,
  forest: 0x33553f,
  hills: 0x9c8459,
  mountains: 0x83848e,
  desert: 0xcbb682,
  tundra: 0xaec1c8,
};

/**
 * Terrain is drawn as a flat fill with a lighter top edge, per the flat-vector
 * direction in docs/05: depth comes from a single light direction rather than
 * from texture.
 *
 * These are multipliers baked as a vertical gradient into the hex *texture*,
 * not applied per hex. Sprite tint multiplies against the texture, so a texture
 * that fades from white at the top to 84% grey at the bottom gives every hex a
 * lit top edge and a shaded lower edge for free — no per-hex cost, no extra draw
 * calls, and it survives the single-texture batching the terrain layer depends on.
 *
 * `TERRAIN_LIT` is 1.0 because tint cannot brighten past the texture: white *is*
 * the full-tint value. The shading works by darkening downward from it.
 */
export const TERRAIN_LIT = 1.0;
export const TERRAIN_SHADE = 0.84;

/**
 * The dark casing stroked *beneath* every kingdom border.
 *
 * This is docs/05's core rule made mechanical rather than hoped for: a coloured
 * line alone is only legible against terrain it happens to contrast with, and
 * some kingdom colours sit very close to some biomes (the yellow against desert,
 * the green against forest). A dark casing underneath guarantees separation from
 * *any* ground, which is the standard cartographic answer and costs one extra
 * stroke pass over geometry that is already built.
 */
export const BORDER_CASING = 0x0a0b0f;

/** Multiplier on border width for the casing pass. */
export const BORDER_CASING_SCALE = 2.1;

/**
 * Kingdom colours: the Okabe-Ito qualitative palette, distinguishable under all
 * three common forms of colour blindness, minus black (unreadable as a
 * territory fill) and plus a neutral grey.
 *
 * Deliberately a *copy* of the server's list in `server/src/game/session.ts`
 * rather than an import — the client must not reach into server code, and
 * kingdom colour arrives on the wire in `KingdomState.color` anyway. This array
 * exists for the cases where the client needs a colour before state has been
 * received (legends, colour pickers, previews).
 */
export const KINGDOM_COLORS: readonly number[] = [
  0x0072b2, // blue
  0xd55e00, // vermillion
  0x009e73, // bluish green
  0xcc79a7, // reddish purple
  0x56b4e9, // sky blue
  0xe69f00, // orange
  0xf0e442, // yellow
  0x999999, // neutral grey
] as const;

/**
 * Multiplied into the terrain fill of unowned hexes to push them visually back,
 * so claimed land reads forward without needing a brighter civ layer. Also the
 * fallback owner colour for a kingdom id the client hasn't been told about yet —
 * a hex owned by an unknown kingdom renders neutral rather than throwing.
 */
export const UNCLAIMED_TINT = 0x8d8f96;

/**
 * The hairline rim baked into the terrain hex texture, as a multiplicative
 * shade. Baking the grid into the texture rather than stroking it per hex is
 * what makes the whole terrain layer one draw call: see `hexRenderer.ts`.
 */
export const NEUTRAL_OUTLINE = 0xb0b2b8;

/** Warm accent shared with the DOM overlay (`--accent` in `ui/style.css`). */
export const ACCENT = 0xe0aa4a;

/** Hover and selection highlights, which must read over any biome. */
export const HOVER_OUTLINE = 0xf4f1e8;
export const SELECTION_OUTLINE = ACCENT;

/** Capture meters. Warm = something is being taken from someone. */
export const CAPTURE_FILL = 0xe8622a;
export const CAPTURE_TRACK = 0x14161c;

/** Claim preview: cool and quiet, so it never reads as combat. */
export const CLAIM_PREVIEW = 0xa8e0d0;

/** Building glyphs: a dark halo under a light glyph, legible on any biome. */
export const BUILDING_GLYPH = 0xf4f1e8;
export const BUILDING_HALO = 0x14161c;

/**
 * Special-hex glyph tints, per type.
 *
 * Cool and luminous, deliberately unlike any kingdom colour in `KINGDOM_COLORS`
 * and unlike the warm `ACCENT`: a special hex must never read as "someone owns
 * this". It is a feature of the world that ownership is drawn *on top of*, per
 * docs/05, so it is tinted like weather rather than like a banner.
 */
export const SPECIAL_GLYPH: Record<SpecialHex, number> = {
  ruins: 0xd8cfa8,
  geyser: 0x8fd8e8,
  crystalVein: 0xc0a8f0,
};

export const SPECIAL_HALO = 0x0f1016;

/**
 * Tint laid over a hex a world event is acting on.
 *
 * Warm and desaturating, applied to the *terrain* sprite rather than as an
 * overlay in `civLayer`, so it reads as something happening to the ground while
 * ownership borders stay exactly as legible on top — the hard rule in docs/05.
 */
export const EVENT_TINT = 0xd2603a;

/** How far toward EVENT_TINT an affected hex is pulled, 0..1. */
export const EVENT_TINT_STRENGTH = 0.45;

/** Ring drawn around a hex braced by the `fortify` god tool. */
export const FORTIFY_RING = 0x8fd8e8;

/**
 * Troop markers.
 *
 * Troop density drives every combat calculation in the game and, until now, was
 * drawn nowhere at all — it existed only as a number in the inspector, and only
 * if you clicked that exact hex. A player could not see where their own army was.
 */
export const TROOP_CASING = 0x08090c;

/**
 * Decoration tints per biome — the trees, rocks and dunes scattered on terrain.
 *
 * Each is a darker, slightly desaturated relative of its biome fill rather than
 * a new hue. Decorations are texture, not information: they should make the
 * ground feel like somewhere while staying quiet enough that ownership fills,
 * troop markers and borders remain the things the eye goes to.
 */
export const DECOR_COLORS: Record<Biome, number> = {
  plains: 0x5c7d46,
  forest: 0x21402c,
  hills: 0x7d6842,
  mountains: 0x63646d,
  desert: 0xb09a68,
  tundra: 0x8fa4ad,
};

/** Highlight on the lit side of each decoration, for the shared light direction. */
export const DECOR_HIGHLIGHT: Record<Biome, number> = {
  plains: 0x86a862,
  forest: 0x3a6647,
  hills: 0xa08a5c,
  mountains: 0x8e8f99,
  desert: 0xd8c48c,
  tundra: 0xc4d6dd,
};

/** Flash colours for the one-off feedback pulses. */
export const FLASH_CAPTURE = 0xfff2d0;
export const FLASH_REJECT = 0xe2705f;

/**
 * Linear blend between two 0xRRGGBB colours in sRGB. Not perceptually correct,
 * which does not matter for tinting placeholder fills, and matters less than
 * being allocation-free on a path the renderer calls per visible hex.
 */
export function blendColors(a: number, b: number, t: number): number {
  const clamped = t < 0 ? 0 : t > 1 ? 1 : t;
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * clamped);
  const g = Math.round(ag + (bg - ag) * clamped);
  const bl = Math.round(ab + (bb - ab) * clamped);
  return (r << 16) | (g << 8) | bl;
}
